import { useCallback, useEffect, useState } from 'react'
import { useDispatch, useSelector } from 'react-redux'
import { availabilityService } from '../../services/availabilityService'
import { MyEntitlementResponse } from '../../types/my-entitlement-response'
import { RootStore } from '../../redux/store'
import { BaseResponse } from '../../types/base-response'
import { showErrorMessage } from '../../redux/reducers/snackbarReducer'
import UserErrorMessage from '../../utils/errorFilter'
import { BuyHoursType } from '../../types/buy-hours-type'
import { SellHoursType } from '../../types/sell-hours-type'

export const useEntitlement = () => {
  const dispatch = useDispatch()
  const { currentEntitlementPeriodResponse } = useSelector((state: RootStore) => state.appSettings)
  const [myEntitlement, setMyEntitlement] = useState<MyEntitlementResponse>()
  const [isEntitlementLoading, setIsEntitlementLoading] = useState<boolean>(true)
  const [buyHours, setBuyHours] = useState<BuyHoursType>()
  const [sellHours, setSellHours] = useState<SellHoursType>()
  const [requestedSellHours, setRequestedSellHours] = useState<number>(0)

  const getEntitlement = useCallback(() => {
    if (!currentEntitlementPeriodResponse) {
      return
    }

    availabilityService
      .getEntitlement(currentEntitlementPeriodResponse.year)
      .then(response => {
        setMyEntitlement(response)
        setIsEntitlementLoading(false)
      })
      .catch(err => {
        setIsEntitlementLoading(false)
        const response: BaseResponse | undefined = err?.response?.data
        response?.errors?.forEach(error => {
          dispatch(showErrorMessage(<UserErrorMessage name={error.name} />))
        })
      })
  }, [currentEntitlementPeriodResponse, dispatch])

  const getAvailabilityData = useCallback(() => {
    if (!currentEntitlementPeriodResponse) {
      return
    }

    availabilityService
      .getMyAvailability(currentEntitlementPeriodResponse.year)
      .then(e => {
        const sellRequests = e.requests.filter(
          x => x.statusDescription === 'Pending' && x.requestType === 'Sell'
        )
        const sellRequested = sellRequests.map(req => req.hours ?? 0).reduce((x, y) => x + y, 0)
        setRequestedSellHours(sellRequested)
      })
      .catch(err => {
        const response: BaseResponse | undefined = err?.response?.data
        response?.errors?.forEach(error => {
          dispatch(showErrorMessage(<UserErrorMessage name={error.name} />))
        })
      })
  }, [currentEntitlementPeriodResponse, dispatch])

  useEffect(() => {
    getEntitlement()
  }, [getEntitlement])

  useEffect(() => {
    if (myEntitlement && !isEntitlementLoading) {
      setBuyHours({
        availableHours: myEntitlement.maxHolidaysAvailableToBuy,
        boughtHours: myEntitlement.hoursBoughtToDate,
        totalHours: myEntitlement.totalHours,
        errors: myEntitlement.errors,
      })
      setSellHours({
        availableHours: myEntitlement.maxHolidaysAvailableToSell,
        soldHours: myEntitlement.hoursSoldToDate,
        totalHours: myEntitlement.totalHours,
        errors: myEntitlement.errors,
      })
    }
  }, [myEntitlement, isEntitlementLoading])

  const refreshEntitlement = useCallback(() => {
    getEntitlement()
    getAvailabilityData()
  }, [getEntitlement, getAvailabilityData])

  return {
    myEntitlement,
    isEntitlementLoading,
    buyHours,
    sellHours,
    requestedSellHours,
    refreshEntitlement,
    fetchSellData: getAvailabilityData,
  }
}
